import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Circle, Users, Calendar, ArrowRight, MessageSquare, Send } from 'lucide-react';
import api from '../api';
import { STATUS_CONFIG, ITEM_TYPE_CONFIG } from '../components/commitments/commitmentConstants';
import { BrandMark } from '../components/BrandMark';

const REFRESH_MS = 60000;

/**
 * Comment box: lets a visitor leave a short note that lands in the
 * commitment owner's Inbox.
 */
function StakeholderCommentCard({ token }) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | sent | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || state === 'sending') return;
    setState('sending');
    try {
      await api.post(`/public/commitments/${token}/comments`, { name: name.trim(), message: message.trim() });
      setState('sent');
    } catch (err) {
      setState(err.response?.status === 429 ? 'limited' : 'error');
    }
  };

  if (state === 'sent') {
    return (
      <div data-testid="public-comment-success" className="rounded-[28px] p-7 text-center animate-fade-in" style={{ background: 'var(--pd-green-soft)', border: '1px solid rgba(21,128,61,0.16)' }}>
        <CheckCircle2 className="w-7 h-7 mx-auto mb-2" style={{ color: 'var(--pd-success)' }} />
        <div className="text-[14px] font-semibold" style={{ color: 'var(--pd-text)' }}>Note delivered</div>
        <p className="text-[12px] mt-1" style={{ color: 'var(--pd-muted)' }}>Your note landed on the owner's desk. They'll see it in their inbox.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-[28px] p-7 animate-fade-in" style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)', animationDelay: '140ms' }}>
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="w-4 h-4" style={{ color: 'var(--pd-focus)' }} />
        <span className="text-[13px] font-semibold" style={{ color: 'var(--pd-text)' }}>Leave a note for the owner</span>
      </div>
      <p className="text-[11px] mb-4" style={{ color: 'var(--pd-subtle)' }}>Questions, updates, or a nudge — delivered straight to their inbox. Not shown publicly.</p>

      <input
        data-testid="public-comment-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={80}
        placeholder="Your name (optional)"
        className="w-full px-3 py-2.5 rounded-xl text-[12px] outline-none mb-2"
        style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)', color: 'var(--pd-text)' }}
      />
      <textarea
        data-testid="public-comment-message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        maxLength={1000}
        rows={3}
        placeholder="Write your note…"
        className="w-full px-3 py-2.5 rounded-xl text-[12px] outline-none resize-none"
        style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)', color: 'var(--pd-text)', lineHeight: 1.6 }}
      />
      <div className="flex items-center justify-between mt-3">
        <span data-testid="public-comment-feedback" className="text-[10px]" style={{ color: state === 'error' || state === 'limited' ? 'var(--pd-crimson)' : 'var(--pd-subtle)' }}>
          {state === 'error' ? 'Could not send — please try again.' : state === 'limited' ? 'Too many notes — try again in a few minutes.' : `${message.length}/1000`}
        </span>
        <button
          data-testid="public-comment-submit"
          type="submit"
          disabled={!message.trim() || state === 'sending'}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-semibold text-white transition-opacity disabled:opacity-50"
          style={{ background: 'var(--pd-focus)' }}
        >
          <Send className="w-3.5 h-3.5" /> {state === 'sending' ? 'Sending…' : 'Send note'}
        </button>
      </div>
    </form>
  );
}

/**
 * Public, read-only commitment status page — /share/commitment/:token
 * No authentication. Shows live progress of a shared commitment's bundled
 * items. Auto-refreshes every 60s so the page stays "live".
 */
export default function PublicCommitmentView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | notfound | error

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/public/commitments/${token}`);
      setData(res.data);
      setState('ready');
    } catch (err) {
      setState(err.response?.status === 404 ? 'notfound' : 'error');
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const cfg = STATUS_CONFIG[data?.status] || STATUS_CONFIG.OPEN;
  const progress = data?.progress || { total: 0, completed: 0, percent: 0 };

  const grouped = (data?.items || []).reduce((acc, item) => {
    const type = item.itemType || 'task';
    (acc[type] = acc[type] || []).push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-10" style={{ background: 'var(--pd-bg)' }} data-testid="public-commitment-page">
      {/* Brand header */}
      <div className="w-full max-w-2xl flex items-center gap-3 mb-8 animate-fade-in">
        <BrandMark variant="mark" size="sm" testId="public-brand-mark" />
        <div>
          <div className="pd-brand-wordmark" style={{ fontSize: 15 }}>
            PolkDes<span className="pd-brand-wordmark-q">Q</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.1em] font-semibold mt-0.5" style={{ color: 'var(--pd-subtle)' }}>Shared commitment status</div>
        </div>
      </div>

      {state === 'loading' ? (
        <div className="w-full max-w-2xl space-y-3">
          <div className="h-40 rounded-[28px] skeleton-shimmer" />
          <div className="h-64 rounded-[28px] skeleton-shimmer" />
        </div>
      ) : state !== 'ready' ? (
        <div className="w-full max-w-md rounded-[28px] p-8 text-center animate-fade-in" style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)' }}>
          <h2 data-testid="public-commitment-error-title" className="text-[18px] font-semibold mb-2" style={{ color: 'var(--pd-text)' }}>
            {state === 'notfound' ? 'This link is no longer active' : 'Something went wrong'}
          </h2>
          <p className="text-[12px]" style={{ color: 'var(--pd-muted)', lineHeight: 1.7 }}>
            {state === 'notfound'
              ? 'The share link may have been revoked by its owner, or the address is incorrect.'
              : 'We could not load this commitment right now. Please try again in a moment.'}
          </p>
        </div>
      ) : (
        <div className="w-full max-w-2xl space-y-4">
          {/* Commitment card */}
          <div className="rounded-[28px] p-7 animate-fade-in" style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)' }}>
            <div className="flex items-center gap-2 mb-3">
              <cfg.icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
              <span data-testid="public-commitment-status" className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-[0.06em]" style={{ background: `${cfg.color}15`, color: cfg.color }}>{cfg.label}</span>
            </div>
            <h1 data-testid="public-commitment-title" className="text-[26px] font-semibold" style={{ color: 'var(--pd-text)', letterSpacing: '-0.03em', lineHeight: 1.25 }}>{data.title}</h1>
            {data.description ? <p className="text-[13px] mt-2" style={{ color: 'var(--pd-muted)', lineHeight: 1.7 }}>{data.description}</p> : null}

            <div className="flex items-center gap-4 mt-4 flex-wrap">
              {data.stakeholder ? (
                <span className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--pd-muted)' }}><Users className="w-3.5 h-3.5" />{data.stakeholder}</span>
              ) : null}
              {data.deadline ? (
                <span className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--pd-muted)' }}><Calendar className="w-3.5 h-3.5" />Due {data.deadline}</span>
              ) : null}
              {(data.tags || []).map((tag) => (
                <span key={tag} className="text-[9px] px-2 py-0.5 rounded-full font-semibold" style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-subtle)' }}>{tag}</span>
              ))}
            </div>

            {/* Progress */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>Progress</span>
                <span data-testid="public-commitment-progress" className="text-[13px] font-semibold tabular-nums" style={{ color: cfg.color }}>
                  {progress.completed}/{progress.total} · {progress.percent}%
                </span>
              </div>
              <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--pd-border)' }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progress.percent}%`, background: cfg.color }} />
              </div>
            </div>
          </div>

          {/* Items checklist */}
          <div className="rounded-[28px] p-7 animate-fade-in" style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)', animationDelay: '80ms' }}>
            {Object.keys(grouped).length === 0 ? (
              <p data-testid="public-commitment-no-items" className="text-[12px] text-center py-6" style={{ color: 'var(--pd-subtle)' }}>No items have been added to this commitment yet.</p>
            ) : (
              <div className="space-y-5">
                {Object.entries(grouped).map(([type, items]) => {
                  const tcfg = ITEM_TYPE_CONFIG[type] || ITEM_TYPE_CONFIG.task;
                  return (
                    <div key={type}>
                      <div className="flex items-center gap-1.5 mb-2.5">
                        <tcfg.icon className="w-3.5 h-3.5" style={{ color: tcfg.color }} />
                        <span className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: tcfg.color }}>{tcfg.label}s</span>
                        <span className="text-[10px] tabular-nums" style={{ color: 'var(--pd-subtle)' }}>{items.length}</span>
                      </div>
                      <div className="space-y-1.5">
                        {items.map((item, idx) => (
                          <div key={`${type}-${idx}`} data-testid={`public-item-${type}-${idx}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl row-enter" style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)', animationDelay: `${Math.min(idx, 10) * 30}ms` }}>
                            {item.done ? (
                              <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--pd-success)' }} />
                            ) : (
                              <Circle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--pd-subtle)' }} />
                            )}
                            <span className="text-[13px] flex-1 min-w-0 truncate" style={{ color: item.done ? 'var(--pd-subtle)' : 'var(--pd-text)', textDecoration: item.done ? 'line-through' : 'none' }}>{item.title}</span>
                            {item.dueDate ? <span className="text-[10px] tabular-nums flex-shrink-0" style={{ color: 'var(--pd-subtle)' }}>{item.dueDate}</span> : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <StakeholderCommentCard token={token} />

          {/* Footer */}
          <div className="flex items-center justify-between px-2 pb-6">
            <span data-testid="public-commitment-updated" className="text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
              Live status · updates automatically{data.updatedAt ? ` · last change ${new Date(data.updatedAt).toLocaleString()}` : ''}
            </span>
            <a
              data-testid="public-commitment-cta"
              href="/"
              className="flex items-center gap-1.5 text-[11px] font-semibold transition-opacity hover:opacity-80"
              style={{ color: 'var(--pd-focus)' }}
            >
              Track your commitments with PolkDesQ <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
